import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Bug, Server, Shield, Activity, Target, BarChart2
} from 'lucide-react';
import { scans, vulnerabilities as vulnApi, workspaces as workspacesApi } from '../../api/client';
import VulnerabilityOSINTInsights from '../../components/VulnerabilityOSINTInsights';
import CVEDetailPanel from '../../components/CVEDetailPanel';
import AttackPathDiagram from '../../components/AttackPathDiagram';
import AttackTreeWorkflow from '../../components/AttackTreeWorkflow';
import AttackChainRanking from '../../components/AttackChainRanking';
import { VulnerabilitiesWelcomeBanner } from '../../components/WelcomeBanner';
import PlanRestrictedButton from '../../components/PlanRestrictedButton';
import { usePlanRestrictions } from '../../hooks/usePlanRestrictions';
import ADAttackWizard from '../../components/wizards/ADAttackWizard';
import ExpertScanWizard from '../../components/wizards/ExpertScanWizard';
import AIScanWizard from '../../components/wizards/AIScanWizard';
import APIScanWizard from '../../components/wizards/APIScanWizard';
import IoTScanWizard from '../../components/wizards/IoTScanWizard';
import OTScanWizard from '../../components/wizards/OTScanWizard';
import { useI18n } from '../../i18n';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

const VulnerabilitiesList = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { workspaceId: activeWorkspaceId, activeWorkspace } = useWorkspace();
  const { isFreePlan } = usePlanRestrictions();
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedCVE, setSelectedCVE] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedVuln, setSelectedVuln] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('severity');
  const [selectedVulns, setSelectedVulns] = useState([]);
  const [availableScans, setAvailableScans] = useState([]);

  // Workspace vulnerabilities for attack tree
  const [workspaceVulns, setWorkspaceVulns] = useState([]);

  // Attack tree states
  const [showAttackTree, setShowAttackTree] = useState(false);
  const [attackTreeData, setAttackTreeData] = useState(null);
  const [attackTreeLoading, setAttackTreeLoading] = useState(false);

  // Attack chain ranking state
  const [showAttackChainRanking, setShowAttackChainRanking] = useState(true);

  // Action panel states
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [actionVuln, setActionVuln] = useState(null);
  const [vulnActions, setVulnActions] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  // Assistant IA states
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiQueryLoading, setAiQueryLoading] = useState(false);

  // PoC Auto states
  const [showPoCPanel, setShowPoCPanel] = useState(false);
  const [selectedPoCVuln, setSelectedPoCVuln] = useState(null);
  const [pocCode, setPocCode] = useState('');
  const [pocLoading, setPocLoading] = useState(false);
  const [pocExecuting, setPocExecuting] = useState(false);
  const [pocResult, setPocResult] = useState(null);

  // Priority Modal states
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [priorityVuln, setPriorityVuln] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [priorityNote, setPriorityNote] = useState('');
  const [prioritySaving, setPrioritySaving] = useState(false);

  // AI Wizard states
  const [showAIWizard, setShowAIWizard] = useState(false);
  const [aiWizardLoading, setAiWizardLoading] = useState(false);
  const [aiWizardRecommendation, setAiWizardRecommendation] = useState(null);
  const [selectedWizard, setSelectedWizard] = useState(null);
  const [wizardVuln, setWizardVuln] = useState(null);

  // Screenshot capture states
  const [screenshots, setScreenshots] = useState([]);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [showScreenshotPanel, setShowScreenshotPanel] = useState(false);
  const canvasRef = useRef(null);

  const openCVEPanel = (vuln) => {
    setSelectedCVE(vuln);
    setIsPanelOpen(true);
  };

  const closeCVEPanel = () => {
    setIsPanelOpen(false);
    setTimeout(() => setSelectedCVE(null), 300);
  };

  // Fetch attack tree when workspace is selected (from sidebar context)
  const fetchAttackTree = useCallback(async (workspaceId) => {
    if (!workspaceId) {
      setAttackTreeData(null);
      return;
    }
    setAttackTreeLoading(true);
    try {
      const data = await workspacesApi.getAttackTree(workspaceId);
      setAttackTreeData(data);
      // Also set workspace vulnerabilities from the attack tree data
      if (data.vulnerabilities) {
        setWorkspaceVulns(data.vulnerabilities);
      }
    } catch (error) {
      console.error('Failed to fetch attack tree:', error);
      setAttackTreeData(null);
    } finally {
      setAttackTreeLoading(false);
    }
  }, []);

  // Auto-fetch attack tree when workspace changes from sidebar
  useEffect(() => {
    if (activeWorkspaceId) {
      fetchAttackTree(activeWorkspaceId);
    } else {
      setAttackTreeData(null);
      setWorkspaceVulns([]);
    }
  }, [activeWorkspaceId, fetchAttackTree]);

  useEffect(() => {
    // Reset immédiatement au changement de workspace
    setVulnerabilities([]);
    setAvailableScans([]);
    setSelectedVulns([]);
    setLoading(true);

    const fetchVulns = async () => {
      try {
        // Filter by active workspace if one is selected
        const scansData = await scans.list(0, 100, activeWorkspaceId);
        const allVulns = [];
        const scansList = [];

        for (const scan of scansData.scans || []) {
          // Store scan info for filter (include name from config)
          scansList.push({
            id: scan.id,
            name: scan.name || scan.config?.name,
            target: scan.target,
            status: scan.status,
            created_at: scan.created_at,
            total_vulnerabilities: scan.total_vulnerabilities || 0,
            workspace_id: scan.workspace_id
          });

          try {
            const vulns = await scans.getVulnerabilities(scan.id);
            allVulns.push(...vulns.map(v => ({
              ...v,
              scan_id: scan.id,
              scan_target: scan.target,
              scan_status: scan.status,
              workspace_id: scan.workspace_id
            })));
          } catch (e) {
            console.error('Failed to fetch vulns for scan:', scan.id);
          }
        }

        setAvailableScans(scansList);
        setVulnerabilities(allVulns);
      } catch (error) {
        console.error('Failed to fetch vulnerabilities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVulns();
  }, [activeWorkspaceId]);

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-severity-critical/20 text-severity-critical border-severity-critical/50',
      high: 'bg-severity-high/20 text-severity-high border-severity-high/50',
      medium: 'bg-severity-medium/20 text-severity-medium border-severity-medium/50',
      low: 'bg-severity-low/20 text-severity-low border-severity-low/50',
      info: 'bg-severity-info/20 text-severity-info border-severity-info/50',
    };
    return colors[severity] || colors.info;
  };

  const getSeverityValue = (severity) => {
    const values = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
    return values[severity] || 0;
  };

  // Filtered and sorted vulnerabilities
  const filteredVulns = vulnerabilities
    .filter(v => filter === 'all' || v.severity === filter)
    .filter(v =>
      searchQuery === '' ||
      v.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.cve_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'severity') return getSeverityValue(b.severity) - getSeverityValue(a.severity);
      if (sortBy === 'cvss') return (b.cvss_score || 0) - (a.cvss_score || 0);
      if (sortBy === 'date') return new Date(b.discovered_at || 0) - new Date(a.discovered_at || 0);
      return 0;
    });

  // Execute action on vulnerability
  const executeVulnAction = async (action, vuln) => {
    setActionLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/v1/vulnerability/${vuln.id}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          action_type: action,
          scan_id: vuln.scan_id,
          target: vuln.host_id || vuln.scan_target
        })
      });

      if (response.ok) {
        const result = await response.json();
        setVulnActions(prev => [...prev, {
          id: Date.now(),
          vuln_id: vuln.id,
          vuln_title: vuln.title,
          action_type: action,
          result: result,
          timestamp: new Date(),
          scan_id: vuln.scan_id
        }]);
      } else {
        // Fallback - simuler l'action
        setVulnActions(prev => [...prev, {
          id: Date.now(),
          vuln_id: vuln.id,
          vuln_title: vuln.title,
          action_type: action,
          result: {
            success: true,
            output: `Action "${action}" simulee sur ${vuln.title}\nCible: ${vuln.host_id || vuln.scan_target}\nStatut: En attente d'integration au rapport`
          },
          timestamp: new Date(),
          scan_id: vuln.scan_id
        }]);
      }
    } catch (error) {
      // Fallback local
      setVulnActions(prev => [...prev, {
        id: Date.now(),
        vuln_id: vuln.id,
        vuln_title: vuln.title,
        action_type: action,
        result: {
          success: true,
          output: `Action "${action}" planifiee sur ${vuln.title}\nCible: ${vuln.host_id || vuln.scan_target}\nSera integree au rapport du scan`
        },
        timestamp: new Date(),
        scan_id: vuln.scan_id
      }]);
    } finally {
      setActionLoading(false);
    }
  };

  // Open action panel for vulnerability
  const openActionPanel = (vuln) => {
    setActionVuln(vuln);
    setShowActionPanel(true);
  };

  // AI Wizard - Analyze vulnerability and recommend wizard
  const analyzeVulnForWizard = async (vuln) => {
    setWizardVuln(vuln);
    setShowAIWizard(true);
    setAiWizardLoading(true);
    setAiWizardRecommendation(null);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/v1/ai/analyze-vulnerability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          cve_id: vuln.cve_id,
          title: vuln.title,
          description: vuln.description,
          severity: vuln.severity,
          cvss_score: vuln.cvss_score,
          target: vuln.host_id || vuln.scan_target,
          port: vuln.port,
          service: vuln.service
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiWizardRecommendation(data);
      } else {
        // Fallback - determine wizard based on CVE/vuln characteristics
        const recommendation = determineWizardFromVuln(vuln);
        setAiWizardRecommendation(recommendation);
      }
    } catch (error) {
      console.error('AI analysis failed:', error);
      const recommendation = determineWizardFromVuln(vuln);
      setAiWizardRecommendation(recommendation);
    } finally {
      setAiWizardLoading(false);
    }
  };

  // Determine wizard based on vulnerability characteristics
  const determineWizardFromVuln = (vuln) => {
    const title = (vuln.title || '').toLowerCase();
    const desc = (vuln.description || '').toLowerCase();
    const cve = (vuln.cve_id || '').toLowerCase();
    const service = (vuln.service || '').toLowerCase();
    const port = vuln.port;

    // AD/Windows related
    if (title.includes('active directory') || title.includes('kerberos') ||
        title.includes('ldap') || title.includes('smb') || title.includes('ntlm') ||
        desc.includes('domain controller') || service.includes('ldap') ||
        port === 389 || port === 636 || port === 445 || port === 88) {
      return {
        wizard: 'ad',
        name: t('ad.attackWizard'),
        reason: "Cette vulnérabilité est liée à Active Directory, Kerberos ou des services Windows. Le wizard AD permet d'exploiter les failles d'authentification et de mouvement latéral.",
        icon: '🏢',
        color: 'purple'
      };
    }

    // API/Web related
    if (title.includes('api') || title.includes('rest') || title.includes('graphql') ||
        title.includes('oauth') || title.includes('jwt') || title.includes('injection') ||
        desc.includes('api endpoint') || service.includes('http')) {
      return {
        wizard: 'api',
        name: 'API Scan Wizard',
        reason: "Cette vulnérabilité concerne une API ou un service web. Le wizard API permet de tester les endpoints, l'authentification et les injections.",
        icon: '🔌',
        color: 'blue'
      };
    }

    // IoT related
    if (title.includes('iot') || title.includes('mqtt') || title.includes('coap') ||
        title.includes('firmware') || title.includes('embedded') ||
        port === 1883 || port === 8883 || port === 5683) {
      return {
        wizard: 'iot',
        name: 'IoT Scan Wizard',
        reason: "Cette vulnérabilité affecte un équipement IoT ou un protocole industriel. Le wizard IoT est spécialisé dans l'analyse des objets connectés.",
        icon: '📡',
        color: 'green'
      };
    }

    // OT/SCADA related
    if (title.includes('scada') || title.includes('modbus') || title.includes('plc') ||
        title.includes('industrial') || title.includes('ics') ||
        port === 502 || port === 102 || port === 44818) {
      return {
        wizard: 'ot',
        name: 'OT/SCADA Wizard',
        reason: "Cette vulnérabilité concerne un système industriel ou SCADA. Le wizard OT est conçu pour l'analyse sécurisée des environnements industriels.",
        icon: '🏭',
        color: 'orange'
      };
    }

    // Expert mode for complex/critical vulns
    if (vuln.severity === 'critical' || (vuln.cvss_score && vuln.cvss_score >= 9.0)) {
      return {
        wizard: 'expert',
        name: 'Expert Scan Wizard',
        reason: "Cette vulnérabilité critique nécessite une analyse approfondie. Le mode Expert offre un contrôle total sur les techniques d'exploitation.",
        icon: '⚡',
        color: 'red'
      };
    }

    // Default to AI wizard
    return {
      wizard: 'ai',
      name: 'AI Scan Wizard',
      reason: "L'IA va analyser cette vulnérabilité et déterminer automatiquement la meilleure stratégie d'exploitation.",
      icon: '🤖',
      color: 'cyan'
    };
  };

  // Launch selected wizard
  const launchWizard = (wizardType, vuln) => {
    setSelectedWizard(wizardType);
    setShowAIWizard(false);
    setShowActionPanel(false);
  };

  // Capture screenshot
  const captureScreenshot = async (vuln, description = '') => {
    setCaptureLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';

      // Capture current viewport as base64
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Use html2canvas-like approach for viewport
      const timestamp = new Date().toISOString();

      // Save screenshot metadata
      const response = await fetch(`${backendUrl}/api/v1/scan/${vuln.scan_id}/capture-screenshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          vuln_id: vuln.id,
          vuln_title: vuln.title,
          cve_id: vuln.cve_id,
          description: description || `Capture de preuve - ${vuln.title}`,
          timestamp: timestamp,
          target: vuln.host_id || vuln.scan_target
        })
      });

      if (response.ok) {
        const result = await response.json();
        setScreenshots(prev => [...prev, result.capture]);
        return result.capture;
      }
    } catch (error) {
      console.error('Screenshot capture failed:', error);
    } finally {
      setCaptureLoading(false);
    }
    return null;
  };

  // Fetch screenshots for scan
  const fetchScreenshots = async (scanId) => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/v1/scan/${scanId}/screenshots`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setScreenshots(data.screenshots || []);
      }
    } catch (error) {
      console.error('Failed to fetch screenshots:', error);
    }
  };

  const severityCounts = {
    critical: vulnerabilities.filter(v => v.severity === 'critical').length,
    high: vulnerabilities.filter(v => v.severity === 'high').length,
    medium: vulnerabilities.filter(v => v.severity === 'medium').length,
    low: vulnerabilities.filter(v => v.severity === 'low').length,
    info: vulnerabilities.filter(v => v.severity === 'info').length,
  };

  // Calculate statistics
  const avgCVSS = vulnerabilities.length > 0
    ? (vulnerabilities.reduce((sum, v) => sum + (v.cvss_score || 0), 0) / vulnerabilities.filter(v => v.cvss_score).length).toFixed(1)
    : 0;
  const uniqueHosts = [...new Set(vulnerabilities.map(v => v.host_id))].length;
  const exploitableCount = vulnerabilities.filter(v => v.cvss_score >= 7).length;

  const handleVulnSelect = (vuln) => {
    setSelectedVuln(vuln);
    setAiAnalysis(null);
  };

  const toggleVulnSelection = (vuln, e) => {
    e.stopPropagation();
    setSelectedVulns(prev =>
      prev.find(v => v.id === vuln.id)
        ? prev.filter(v => v.id !== vuln.id)
        : [...prev, vuln]
    );
  };

  // AI Analysis for selected vulnerability
  const requestAiAnalysis = async () => {
    if (!selectedVuln) return;
    setAiLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setAiAnalysis({
        summary: `Cette vulnérabilité ${selectedVuln.severity} affecte ${selectedVuln.scan_target || 'le système cible'}. Elle permet potentiellement à un attaquant d'exploiter ${selectedVuln.title?.toLowerCase() || 'une faille de sécurité'}.`,
        impact: selectedVuln.severity === 'critical' ? 'Impact critique sur la confidentialité et l\'intégrité du système' :
                selectedVuln.severity === 'high' ? 'Impact élevé - nécessite une attention immédiate' :
                selectedVuln.severity === 'medium' ? 'Impact modéré - à corriger dans les meilleurs délais' :
                'Impact faible - à surveiller',
        recommendations: [
          'Appliquer les correctifs de sécurité disponibles',
          'Restreindre l\'accès réseau au service affecté',
          'Mettre en place une surveillance renforcée',
          selectedVuln.cve_id ? `Consulter les détails CVE: ${selectedVuln.cve_id}` : 'Effectuer une analyse approfondie'
        ],
        exploitability: selectedVuln.cvss_score ? (selectedVuln.cvss_score >= 7 ? 'Élevée' : selectedVuln.cvss_score >= 4 ? 'Moyenne' : 'Faible') : 'Non évaluée',
        attackVector: selectedVuln.cvss_score >= 8 ? 'Réseau (Remote)' : selectedVuln.cvss_score >= 5 ? 'Adjacent' : 'Local',
        mitreTechniques: getMitreTechniques(selectedVuln)
      });
    } catch (error) {
      console.error('AI analysis failed:', error);
    } finally {
      setAiLoading(false);
    }
  };

  // Get MITRE techniques based on vulnerability
  const getMitreTechniques = (vuln) => {
    const techniques = [];
    const title = (vuln.title || '').toLowerCase();

    if (title.includes('sql') || title.includes('injection')) techniques.push({ id: 'T1190', name: 'Exploit Public-Facing Application' });
    if (title.includes('rce') || title.includes('remote code')) techniques.push({ id: 'T1059', name: 'Command and Scripting Interpreter' });
    if (title.includes('xss') || title.includes('cross-site')) techniques.push({ id: 'T1189', name: 'Drive-by Compromise' });
    if (title.includes('auth') || title.includes('bypass')) techniques.push({ id: 'T1078', name: 'Valid Accounts' });
    if (title.includes('priv') || title.includes('escalation')) techniques.push({ id: 'T1068', name: 'Exploitation for Privilege Escalation' });
    if (title.includes('buffer') || title.includes('overflow')) techniques.push({ id: 'T1203', name: 'Exploitation for Client Execution' });
    if (title.includes('smb') || title.includes('eternalblue')) techniques.push({ id: 'T1210', name: 'Exploitation of Remote Services' });

    if (techniques.length === 0) techniques.push({ id: 'T1190', name: 'Exploit Public-Facing Application' });
    return techniques;
  };

  // AI Assistant Query Handler
  const handleAIQuery = useCallback(async (query) => {
    setAiQueryLoading(true);
    setAiResponse(null);

    try {
      const isDemo = process.env.REACT_APP_DEMO_MODE === 'true';
      const endpoint = isDemo ? '/api/v1/ai/chat-demo' : '/api/v1/ai/query';
      const body = isDemo
        ? { question: query }
        : {
            query,
            context: {
              type: 'vulnerabilities',
              total: vulnerabilities.length,
              critical: severityCounts.critical,
              high: severityCounts.high,
              medium: severityCounts.medium,
              low: severityCounts.low,
              vulnerabilities: vulnerabilities.slice(0, 20).map(v => ({
                title: v.title,
                severity: v.severity,
                cve_id: v.cve_id,
                cvss_score: v.cvss_score,
                host: v.host_id
              }))
            }
          };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        // Demo returns `answer`; prod returns `response`.
        setAiResponse(data.answer ?? data.response);
      } else {
        setAiResponse(generateLocalAIResponse(query));
      }
    } catch (error) {
      setAiResponse(generateLocalAIResponse(query));
    } finally {
      setAiQueryLoading(false);
    }
  }, [vulnerabilities, severityCounts]);

  // Local AI fallback
  const generateLocalAIResponse = (query) => {
    const q = query.toLowerCase();

    if (q.includes('priorit') || q.includes('urgent') || q.includes('important')) {
      return `**Priorisation des vulnérabilités:**\n\n` +
        `1. **Critiques (${severityCounts.critical})** - Action immédiate requise\n` +
        `2. **Hautes (${severityCounts.high})** - Corriger sous 24-48h\n` +
        `3. **Moyennes (${severityCounts.medium})** - Planifier correction\n` +
        `4. **Basses (${severityCounts.low})** - Surveillance\n\n` +
        `Focus sur les vulnérabilités avec CVSS >= 7.0 (${exploitableCount} identifiées)`;
    }

    if (q.includes('exploit') || q.includes('attaq') || q.includes('poc')) {
      const criticals = vulnerabilities.filter(v => v.severity === 'critical');
      return `**Vulnérabilités exploitables:**\n\n` +
        `${exploitableCount} vulnérabilités avec CVSS >= 7.0\n\n` +
        `**Top vulnérabilités critiques:**\n` +
        criticals.slice(0, 5).map((v, i) => `${i+1}. ${v.title} (${v.cve_id || 'N/A'}) - CVSS: ${v.cvss_score || 'N/A'}`).join('\n') +
        `\n\nUtilisez le panneau PoC Auto pour générer des codes d'exploitation.`;
    }

    if (q.includes('rapport') || q.includes('summary') || q.includes('résumé')) {
      return `**Résumé des vulnérabilités:**\n\n` +
        `- Total: ${vulnerabilities.length} vulnérabilités\n` +
        `- Critiques: ${severityCounts.critical}\n` +
        `- Hautes: ${severityCounts.high}\n` +
        `- Moyennes: ${severityCounts.medium}\n` +
        `- Basses: ${severityCounts.low}\n` +
        `- CVSS moyen: ${avgCVSS}\n` +
        `- Hôtes affectés: ${uniqueHosts}\n\n` +
        `**Recommandation:** Prioriser les ${severityCounts.critical + severityCounts.high} vulnérabilités critiques/hautes.`;
    }

    if (q.includes('remédi') || q.includes('correct') || q.includes('fix')) {
      return `**Plan de remédiation suggéré:**\n\n` +
        `1. **Phase 1 - Urgente (J+1)**\n` +
        `   - Corriger les ${severityCounts.critical} vulnérabilités critiques\n` +
        `   - Isoler les systèmes compromis\n\n` +
        `2. **Phase 2 - Prioritaire (J+7)**\n` +
        `   - Traiter les ${severityCounts.high} vulnérabilités hautes\n` +
        `   - Appliquer les patches de sécurité\n\n` +
        `3. **Phase 3 - Planifiée (J+30)**\n` +
        `   - Corriger les vulnérabilités moyennes\n` +
        `   - Renforcer la configuration`;
    }

    return `**Analyse des vulnérabilités:**\n\n` +
      `Votre infrastructure présente ${vulnerabilities.length} vulnérabilités dont ${severityCounts.critical + severityCounts.high} critiques/hautes nécessitant une attention immédiate.\n\n` +
      `**Actions recommandées:**\n` +
      `1. Prioriser les corrections par sévérité\n` +
      `2. Utiliser le PoC Auto pour valider les failles\n` +
      `3. Générer un rapport détaillé\n` +
      `4. Mettre en place un suivi de remédiation`;
  };

  // PoC Generation
  const generatePoC = useCallback(async (vuln) => {
    setSelectedPoCVuln(vuln);
    setPocLoading(true);
    setPocCode('');
    setPocResult(null);

    try {
      const response = await fetch('/api/v1/ai/generate-poc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          vulnerability: {
            title: vuln.title,
            cve_id: vuln.cve_id,
            severity: vuln.severity,
            cvss_score: vuln.cvss_score,
            description: vuln.description,
            host: vuln.host_id,
            target: vuln.scan_target
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPocCode(data.poc_code);
      } else {
        setPocCode(generateLocalPoC(vuln));
      }
    } catch (error) {
      setPocCode(generateLocalPoC(vuln));
    } finally {
      setPocLoading(false);
    }
  }, []);

  // Local PoC Generation
  const generateLocalPoC = (vuln) => {
    const title = (vuln.title || '').toLowerCase();
    const host = vuln.host_id || vuln.scan_target || 'TARGET_IP';
    const cve = vuln.cve_id || 'UNKNOWN';

    if (title.includes('sql') || title.includes('injection')) {
      return `#!/usr/bin/env python3
# PoC: SQL Injection - ${cve}
# Cible: ${host}
# AVERTISSEMENT: Usage autorisé uniquement

import requests
import sys

TARGET = "${host}"
PAYLOAD = "' OR '1'='1' --"

def test_sqli():
    """Test SQL Injection basique"""
    endpoints = ['/login', '/search', '/api/users', '/api/query']

    for endpoint in endpoints:
        url = f"http://{TARGET}{endpoint}"

        # Test GET parameter
        params = {'id': PAYLOAD, 'q': PAYLOAD, 'search': PAYLOAD}
        try:
            r = requests.get(url, params=params, timeout=10)
            if 'error' in r.text.lower() or 'sql' in r.text.lower():
                print(f"[+] Potentielle SQLi: {url}")
                print(f"    Status: {r.status_code}")
                return True
        except:
            pass

        # Test POST
        data = {'username': PAYLOAD, 'password': PAYLOAD}
        try:
            r = requests.post(url, data=data, timeout=10)
            if r.status_code != 404:
                print(f"[*] Endpoint trouvé: {url}")
        except:
            pass

    print("[-] Aucune SQLi détectée (test basique)")
    return False

if __name__ == "__main__":
    print(f"=== PoC SQL Injection - {cve} ===")
    print(f"Target: {TARGET}")
    test_sqli()`;
    }

    if (title.includes('xss') || title.includes('cross-site')) {
      return `#!/usr/bin/env python3
# PoC: Cross-Site Scripting (XSS) - ${cve}
# Cible: ${host}
# AVERTISSEMENT: Usage autorisé uniquement

import requests
from urllib.parse import quote

TARGET = "${host}"
PAYLOADS = [
    '<script>alert("XSS")</script>',
    '<img src=x onerror=alert("XSS")>',
    '"><script>alert(document.cookie)</script>',
    "'-alert('XSS')-'",
    '<svg onload=alert("XSS")>'
]

def test_xss():
    """Test XSS basique sur paramètres GET"""
    endpoints = ['/', '/search', '/comment', '/api/echo']

    for endpoint in endpoints:
        for payload in PAYLOADS:
            url = f"http://{TARGET}{endpoint}?q={quote(payload)}"
            try:
                r = requests.get(url, timeout=10)
                if payload in r.text or 'alert' in r.text:
                    print(f"[+] XSS Réfléchi détecté!")
                    print(f"    URL: {url}")
                    print(f"    Payload: {payload}")
                    return True
            except Exception as e:
                pass

    print("[-] Aucun XSS réfléchi détecté (test basique)")
    return False

if __name__ == "__main__":
    print(f"=== PoC XSS - {cve} ===")
    print(f"Target: {TARGET}")
    test_xss()`;
    }

    if (title.includes('rce') || title.includes('remote code') || title.includes('command')) {
      return `#!/usr/bin/env python3
# PoC: Remote Code Execution - ${cve}
# Cible: ${host}
# AVERTISSEMENT: Usage autorisé uniquement

import requests
import sys

TARGET = "${host}"

# Payloads de test RCE (non-destructifs)
PAYLOADS = [
    '; id',
    '| id',
    '\`id\`',
    '$(id)',
    '; whoami',
    '| whoami'
]

def test_rce():
    """Test RCE via injection de commande"""
    endpoints = ['/ping', '/exec', '/cmd', '/api/run', '/api/execute']

    for endpoint in endpoints:
        url = f"http://{TARGET}{endpoint}"

        for payload in PAYLOADS:
            # Test GET
            params = {'cmd': payload, 'host': f'127.0.0.1{payload}', 'ip': f'localhost{payload}'}
            try:
                r = requests.get(url, params=params, timeout=10)
                if 'uid=' in r.text or 'root' in r.text.lower():
                    print(f"[+] RCE DÉTECTÉ!")
                    print(f"    URL: {url}")
                    print(f"    Payload: {payload}")
                    print(f"    Output: {r.text[:200]}")
                    return True
            except:
                pass

            # Test POST
            data = {'command': payload, 'input': payload}
            try:
                r = requests.post(url, data=data, timeout=10)
                if 'uid=' in r.text:
                    print(f"[+] RCE via POST détecté: {url}")
                    return True
            except:
                pass

    print("[-] Aucun RCE détecté (test basique)")
    return False

if __name__ == "__main__":
    print(f"=== PoC RCE - {cve} ===")
    print(f"Target: {TARGET}")
    test_rce()`;
    }

    if (title.includes('smb') || title.includes('eternalblue') || title.includes('ms17')) {
      return `#!/usr/bin/env python3
# PoC: SMB/EternalBlue Check - ${cve}
# Cible: ${host}
# AVERTISSEMENT: Usage autorisé uniquement

import socket
import struct

TARGET = "${host}"
PORT = 445

def check_smb_vulnerable():
    """Vérifie si SMB est vulnérable à EternalBlue"""
    try:
        sock = socket.socket(socket.AF_INET, socket.SOCK_STREAM)
        sock.settimeout(10)
        sock.connect((TARGET, PORT))

        # SMB Negotiate Request
        negotiate = bytes.fromhex(
            '00000054ff534d4272000000001843c80000000000000000000000000000'
            'fffe00000000003100024e54204c4d20302e313200024e54204c414e4d41'
            '4e20312e3000024e54204c414e4d414e20322e3000024e54204c4d20302e313200'
        )

        sock.send(negotiate)
        response = sock.recv(1024)
        sock.close()

        if len(response) > 0:
            print(f"[+] SMB actif sur {TARGET}:445")
            print("[*] Pour test complet, utilisez: nmap --script smb-vuln-ms17-010 {TARGET}")
            return True

    except socket.timeout:
        print(f"[-] Timeout connexion SMB")
    except Exception as e:
        print(f"[-] Erreur: {e}")

    return False

if __name__ == "__main__":
    print(f"=== PoC SMB Check - {cve} ===")
    print(f"Target: {TARGET}")
    check_smb_vulnerable()
    print("\\n[*] Commande nmap recommandée:")
    print(f"    nmap -p445 --script smb-vuln-* {TARGET}")`;
    }

    // Default generic PoC
    return `#!/usr/bin/env python3
# PoC: ${vuln.title || 'Vulnerability Test'} - ${cve}
# Cible: ${host}
# Sévérité: ${vuln.severity?.toUpperCase() || 'UNKNOWN'}
# CVSS: ${vuln.cvss_score || 'N/A'}
# AVERTISSEMENT: Usage autorisé uniquement

import requests
import socket
import sys

TARGET = "${host}"

def scan_ports():
    """Scan des ports communs"""
    common_ports = [21, 22, 23, 25, 80, 443, 445, 3306, 3389, 5432, 8080, 8443]
    open_ports = []

    print(f"[*] Scan de {TARGET}...")
    for port in common_ports:
        try:
            sock = socket.socket(socket.AF_INET, socket.SOCK_STREAM)
            sock.settimeout(2)
            result = sock.connect_ex((TARGET, port))
            if result == 0:
                print(f"    [+] Port {port} ouvert")
                open_ports.append(port)
            sock.close()
        except:
            pass

    return open_ports

def check_http_services():
    """Vérifie les services HTTP"""
    ports = [80, 443, 8080, 8443]

    for port in ports:
        protocol = 'https' if port in [443, 8443] else 'http'
        url = f"{protocol}://{TARGET}:{port}"
        try:
            r = requests.get(url, timeout=5, verify=False)
            print(f"[+] Service HTTP sur {url}")
            print(f"    Status: {r.status_code}")
            print(f"    Server: {r.headers.get('Server', 'Unknown')}")
        except:
            pass

def main():
    print(f"=== PoC: {cve} ===")
    print(f"Vulnérabilité: ${vuln.title || 'Unknown'}")
    print(f"Cible: {TARGET}")
    print(f"Sévérité: ${vuln.severity?.toUpperCase() || 'Unknown'}")
    print("-" * 50)

    open_ports = scan_ports()

    if 80 in open_ports or 443 in open_ports:
        print("\\n[*] Services HTTP détectés:")
        check_http_services()

    print("\\n[*] Recommandations:")
    print("    1. Analyser les services exposés")
    print("    2. Rechercher les exploits connus pour ${cve}")
    print("    3. Vérifier les configurations de sécurité")
    print("    4. Appliquer les patches disponibles")

if __name__ == "__main__":
    main()`;
  };

  // Execute PoC
  const executePoC = async () => {
    if (!pocCode || !selectedPoCVuln) return;
    setPocExecuting(true);
    setPocResult(null);

    try {
      const response = await fetch('/api/v1/poc/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          code: pocCode,
          target: selectedPoCVuln.host_id || selectedPoCVuln.scan_target,
          vulnerability_id: selectedPoCVuln.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPocResult(data);
      } else {
        setPocResult({
          success: false,
          output: 'Exécution simulée - Backend non disponible\n\n' +
            `[*] PoC pour: ${selectedPoCVuln.title}\n` +
            `[*] Cible: ${selectedPoCVuln.host_id || selectedPoCVuln.scan_target}\n` +
            `[*] Résultat: Test terminé (simulation)\n` +
            `[!] Note: Connectez le backend pour exécution réelle`
        });
      }
    } catch (error) {
      setPocResult({
        success: false,
        output: `Erreur d'exécution: ${error.message}\n\nUtilisez ce code en environnement contrôlé.`
      });
    } finally {
      setPocExecuting(false);
    }
  };

  // Priority Modal functions
  const openPriorityModal = (vuln) => {
    setPriorityVuln(vuln);
    setSelectedPriority(vuln.custom_priority || null);
    setPriorityNote(vuln.priority_note || '');
    setShowPriorityModal(true);
  };

  const closePriorityModal = () => {
    setShowPriorityModal(false);
    setPriorityVuln(null);
    setSelectedPriority(null);
    setPriorityNote('');
  };

  const savePriority = async () => {
    if (!priorityVuln) return;
    setPrioritySaving(true);

    try {
      const updated = await vulnApi.updatePriority(priorityVuln.id, selectedPriority, priorityNote || null);
      // Update local state
      setVulnerabilities(prev => prev.map(v =>
        v.id === priorityVuln.id
          ? { ...v, custom_priority: selectedPriority, priority_note: priorityNote }
          : v
      ));
      closePriorityModal();
    } catch (error) {
      console.error('Failed to update priority:', error);
      alert('Erreur lors de la mise à jour de la priorité');
    } finally {
      setPrioritySaving(false);
    }
  };

  // Get priority display info
  const getPriorityInfo = (vuln) => {
    const priority = vuln.custom_priority;
    if (priority === null || priority === undefined) {
      // Auto mode - use severity
      return { label: t('vulnerabilities.priority.auto'), color: 'text-gray-400', bgColor: 'bg-gray-500/20', borderColor: 'border-gray-500/50' };
    }
    const priorities = {
      1: { label: t('vulnerabilities.severities.critical'), color: 'text-red-500', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/50' },
      2: { label: t('vulnerabilities.severities.high'), color: 'text-orange-500', bgColor: 'bg-orange-500/20', borderColor: 'border-orange-500/50' },
      3: { label: t('vulnerabilities.severities.medium'), color: 'text-yellow-500', bgColor: 'bg-yellow-500/20', borderColor: 'border-yellow-500/50' },
      4: { label: t('vulnerabilities.severities.low'), color: 'text-blue-400', bgColor: 'bg-blue-500/20', borderColor: 'border-blue-500/50' },
      5: { label: t('vulnerabilities.severities.info'), color: 'text-gray-400', bgColor: 'bg-gray-500/20', borderColor: 'border-gray-500/50' },
    };
    return priorities[priority] || priorities[5];
  };

  // Bulk actions
  const exportSelectedJSON = () => {
    const data = selectedVulns.length > 0 ? selectedVulns : filteredVulns;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vulnerabilities_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  const exportCSV = () => {
    const data = selectedVulns.length > 0 ? selectedVulns : filteredVulns;
    const headers = ['ID', 'Title', 'Severity', 'CVE', 'CVSS', 'Host', 'Discovered'];
    const rows = data.map(v => [
      v.id, v.title, v.severity, v.cve_id || '', v.cvss_score || '', v.host_id, v.discovered_at || ''
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vulnerabilities_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-brand-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto">
        {/* Welcome Banner */}
        <VulnerabilitiesWelcomeBanner />

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gradient-green">{t('vulnerabilities.title')}</h1>
              <p className="text-gray-400 mt-2">{t('vulnerabilities.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAttackChainRanking(!showAttackChainRanking)}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  showAttackChainRanking
                    ? 'bg-brand-green text-brand-black'
                    : 'bg-brand-green/20 text-brand-green border border-brand-green/50 hover:bg-brand-green/30'
                }`}
              >
                <span>⚔️</span> Attack Chain
              </button>
              <button
                onClick={() => setShowAIAssistant(!showAIAssistant)}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  showAIAssistant
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-500/20 text-purple-400 border border-purple-500/50 hover:bg-purple-500/30'
                }`}
              >
                <span>🤖</span> Assistant IA
              </button>
              <PlanRestrictedButton
                feature="poc_generation"
                onClick={() => setShowPoCPanel(!showPoCPanel)}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  showPoCPanel
                    ? 'bg-red-500 text-white'
                    : 'bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30'
                }`}
                disabledClassName="!bg-gray-700/50 !text-gray-500 !border-gray-600 cursor-not-allowed"
              >
                <span>💥</span> PoC Auto
              </PlanRestrictedButton>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          {/* Total Vulnerabilities */}
          <div className="bg-gradient-to-br from-brand-black-light to-brand-black border border-brand-green/30 rounded-xl p-4 hover:border-brand-green/50 transition-all relative group cursor-help">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{t('vulnerabilities.attackChain.totalVulns')}</span>
              <Bug className="w-5 h-5 text-brand-green" />
            </div>
            <div className="text-3xl font-bold text-white">{vulnerabilities.length}</div>
            <div className="text-xs text-gray-500 mt-1">Vulnérabilités</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-brand-green/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Bug className="w-4 h-4 text-brand-green" />
                <span className="font-semibold text-white">Total Vulnérabilités</span>
              </div>
              <p className="text-xs text-gray-300">Nombre total de CVE (Common Vulnerabilities and Exposures) détectées sur l'ensemble des scans. Chaque CVE représente une faille de sécurité connue.</p>
            </div>
          </div>

          {/* Critical */}
          <div className="bg-gradient-to-br from-red-900/30 to-brand-black border border-red-500/30 rounded-xl p-4 hover:border-red-500/50 transition-all relative group cursor-help">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{t('vulnerabilities.severities.critical')}</span>
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <div className="text-3xl font-bold text-red-500">{severityCounts.critical}</div>
            <div className="text-xs text-red-500/60 mt-1">CVSS 9.0-10.0</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-brand-black border border-red-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="font-semibold text-white">Vulnérabilités Critiques</span>
              </div>
              <p className="text-xs text-gray-300">CVSS 9.0-10.0 : Failles permettant une compromission totale du système (exécution de code à distance, élévation de privilèges root). <span className="text-red-400 font-semibold">Action immédiate requise.</span></p>
            </div>
          </div>

          {/* High */}
          <div className="bg-gradient-to-br from-orange-900/30 to-brand-black border border-orange-500/30 rounded-xl p-4 hover:border-orange-500/50 transition-all relative group cursor-help">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{t('vulnerabilities.severities.high')}</span>
              <Shield className="w-5 h-5 text-orange-500" />
            </div>
            <div className="text-3xl font-bold text-orange-500">{severityCounts.high}</div>
            <div className="text-xs text-orange-500/60 mt-1">CVSS 7.0-8.9</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-brand-black border border-orange-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-orange-500" />
                <span className="font-semibold text-white">Vulnérabilités Élevées</span>
              </div>
              <p className="text-xs text-gray-300">CVSS 7.0-8.9 : Failles pouvant entraîner un accès non autorisé, une fuite de données sensibles ou un déni de service majeur. <span className="text-orange-400 font-semibold">Correction prioritaire recommandée.</span></p>
            </div>
          </div>

          {/* Medium */}
          <div className="bg-gradient-to-br from-yellow-900/30 to-brand-black border border-yellow-500/30 rounded-xl p-4 hover:border-yellow-500/50 transition-all relative group cursor-help">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{t('vulnerabilities.severities.medium')}</span>
              <Activity className="w-5 h-5 text-yellow-500" />
            </div>
            <div className="text-3xl font-bold text-yellow-500">{severityCounts.medium}</div>
            <div className="text-xs text-yellow-500/60 mt-1">CVSS 4.0-6.9</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-brand-black border border-yellow-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-yellow-500" />
                <span className="font-semibold text-white">Vulnérabilités Moyennes</span>
              </div>
              <p className="text-xs text-gray-300">CVSS 4.0-6.9 : Failles nécessitant des conditions spécifiques pour être exploitées (interaction utilisateur, accès local). À corriger dans un délai raisonnable.</p>
            </div>
          </div>

          {/* Low */}
          <div className="bg-gradient-to-br from-blue-900/30 to-brand-black border border-blue-500/30 rounded-xl p-4 hover:border-blue-500/50 transition-all relative group cursor-help">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{t('vulnerabilities.severities.low')}</span>
              <Target className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-bold text-blue-400">{severityCounts.low}</div>
            <div className="text-xs text-blue-400/60 mt-1">CVSS 0.1-3.9</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-blue-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-blue-400" />
                <span className="font-semibold text-white">Vulnérabilités Basses</span>
              </div>
              <p className="text-xs text-gray-300">CVSS 0.1-3.9 : Failles à faible impact, souvent informatives ou nécessitant un accès physique. À traiter lors des maintenances planifiées.</p>
            </div>
          </div>

          {/* Average CVSS */}
          <div className="bg-gradient-to-br from-brand-green/20 to-brand-black border border-brand-green/30 rounded-xl p-4 hover:border-brand-green/50 transition-all relative group cursor-help">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{t('vulnerabilities.attackChain.avgCvss')}</span>
              <BarChart2 className="w-5 h-5 text-brand-green" />
            </div>
            <div className="text-3xl font-bold text-brand-green">{avgCVSS}</div>
            <div className="text-xs text-brand-green/60 mt-1">Score moyen</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-brand-black border border-brand-green/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <BarChart2 className="w-4 h-4 text-brand-green" />
                <span className="font-semibold text-white">Score CVSS Moyen</span>
              </div>
              <p className="text-xs text-gray-300">Moyenne des scores CVSS (Common Vulnerability Scoring System) de toutes les vulnérabilités. Un score élevé (7+) indique un risque global important nécessitant une attention prioritaire.</p>
            </div>
          </div>

          {/* Affected Hosts */}
          <div className="bg-gradient-to-br from-purple-900/30 to-brand-black border border-purple-500/30 rounded-xl p-4 hover:border-purple-500/50 transition-all relative group cursor-help">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{t('vulnerabilities.attackChain.affectedHosts')}</span>
              <Server className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-3xl font-bold text-purple-400">{uniqueHosts}</div>
            <div className="text-xs text-purple-400/60 mt-1">Hôtes uniques</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-purple-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Server className="w-4 h-4 text-purple-400" />
                <span className="font-semibold text-white">Hôtes Affectés</span>
              </div>
              <p className="text-xs text-gray-300">Nombre de machines uniques présentant au moins une vulnérabilité. Plus ce nombre est élevé, plus la surface d'attaque de votre infrastructure est grande.</p>
            </div>
          </div>
        </div>

        {/* Severity Charts Section */}
        {vulnerabilities.length > 0 && (
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Bar Chart */}
            <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <span>📊</span> {t('vulnerabilities.distributionBySeverity')}
              </h3>
              <div className="space-y-3">
                {[
                  { label: t('vulnerabilities.severities.critical'), count: severityCounts.critical, color: 'bg-red-500', textColor: 'text-red-500' },
                  { label: t('vulnerabilities.severities.high'), count: severityCounts.high, color: 'bg-orange-500', textColor: 'text-orange-500' },
                  { label: t('vulnerabilities.severities.medium'), count: severityCounts.medium, color: 'bg-yellow-500', textColor: 'text-yellow-500' },
                  { label: t('vulnerabilities.severities.low'), count: severityCounts.low, color: 'bg-blue-400', textColor: 'text-blue-400' },
                  { label: t('vulnerabilities.severities.info'), count: severityCounts.info, color: 'bg-gray-400', textColor: 'text-gray-400' }
                ].map((item, idx) => {
                  const maxCount = Math.max(severityCounts.critical, severityCounts.high, severityCounts.medium, severityCounts.low, severityCounts.info, 1);
                  const percentage = (item.count / maxCount) * 100;
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <span className={`text-sm font-medium w-24 ${item.textColor}`}>{item.label}</span>
                      <div className="flex-1 h-6 bg-brand-black rounded-full overflow-hidden relative">
                        <div
                          className={`h-full ${item.color} transition-all duration-500 ease-out rounded-full`}
                          style={{ width: `${percentage}%` }}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-white">
                          {item.count}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Radar/Spider Chart */}
            <div className="bg-brand-black-light rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <span>🕸️</span> Graphique en Toile (Radar)
              </h3>
              <div className="flex items-center justify-center">
                <svg viewBox="0 0 200 200" className="w-48 h-48">
                  {/* Background grid */}
                  {[20, 40, 60, 80, 100].map((r, idx) => (
                    <polygon
                      key={idx}
                      points={
                        [0, 72, 144, 216, 288].map(angle => {
                          const rad = (angle - 90) * Math.PI / 180;
                          const x = 100 + r * Math.cos(rad);
                          const y = 100 + r * Math.sin(rad);
                          return `${x},${y}`;
                        }).join(' ')
                      }
                      fill="none"
                      stroke="#374151"
                      strokeWidth="1"
                    />
                  ))}
                  {/* Axis lines */}
                  {[0, 72, 144, 216, 288].map((angle, idx) => {
                    const rad = (angle - 90) * Math.PI / 180;
                    const x = 100 + 100 * Math.cos(rad);
                    const y = 100 + 100 * Math.sin(rad);
                    return (
                      <line key={idx} x1="100" y1="100" x2={x} y2={y} stroke="#374151" strokeWidth="1" />
                    );
                  })}
                  {/* Data polygon */}
                  {(() => {
                    const critical = severityCounts.critical || 0;
                    const high = severityCounts.high || 0;
                    const medium = severityCounts.medium || 0;
                    const low = severityCounts.low || 0;
                    const info = severityCounts.info || 0;
                    const maxVal = Math.max(critical, high, medium, low, info, 1);
                    const values = [
                      critical / maxVal,
                      high / maxVal,
                      medium / maxVal,
                      low / maxVal,
                      info / maxVal
                    ];
                    const points = [0, 72, 144, 216, 288].map((angle, idx) => {
                      const rad = (angle - 90) * Math.PI / 180;
                      const r = (values[idx] || 0) * 80 + 10;
                      const x = 100 + r * Math.cos(rad);
                      const y = 100 + r * Math.sin(rad);
                      return `${x},${y}`;
                    }).join(' ');
                    return (
                      <polygon
                        points={points}
                        fill="rgba(0, 255, 128, 0.2)"
                        stroke="#00ff80"
                        strokeWidth="2"
                      />
                    );
                  })()}
                  {/* Data points */}
                  {(() => {
                    const critical = severityCounts.critical || 0;
                    const high = severityCounts.high || 0;
                    const medium = severityCounts.medium || 0;
                    const low = severityCounts.low || 0;
                    const info = severityCounts.info || 0;
                    const maxVal = Math.max(critical, high, medium, low, info, 1);
                    const values = [
                      critical / maxVal,
                      high / maxVal,
                      medium / maxVal,
                      low / maxVal,
                      info / maxVal
                    ];
                    const colors = ['#ef4444', '#f97316', '#eab308', '#60a5fa', '#9ca3af'];
                    return [0, 72, 144, 216, 288].map((angle, idx) => {
                      const rad = (angle - 90) * Math.PI / 180;
                      const r = (values[idx] || 0) * 80 + 10;
                      const x = 100 + r * Math.cos(rad);
                      const y = 100 + r * Math.sin(rad);
                      return (
                        <circle key={idx} cx={x} cy={y} r="5" fill={colors[idx]} stroke="white" strokeWidth="2" />
                      );
                    });
                  })()}
                  {/* Labels */}
                  {[
                    { angle: 0, label: t('vulnerabilities.severities.critical'), color: '#ef4444' },
                    { angle: 72, label: t('vulnerabilities.severities.high'), color: '#f97316' },
                    { angle: 144, label: t('vulnerabilities.severities.medium'), color: '#eab308' },
                    { angle: 216, label: t('vulnerabilities.severities.low'), color: '#60a5fa' },
                    { angle: 288, label: t('vulnerabilities.severities.info'), color: '#9ca3af' }
                  ].map((item, idx) => {
                    const rad = (item.angle - 90) * Math.PI / 180;
                    const x = 100 + 115 * Math.cos(rad);
                    const y = 100 + 115 * Math.sin(rad);
                    return (
                      <text
                        key={idx}
                        x={x}
                        y={y}
                        fill={item.color}
                        fontSize="10"
                        fontWeight="bold"
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        {item.label}
                      </text>
                    );
                  })}
                </svg>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-4">
                {[
                  { label: t('vulnerabilities.severities.critical'), count: severityCounts.critical, color: 'bg-red-500' },
                  { label: t('vulnerabilities.severities.high'), count: severityCounts.high, color: 'bg-orange-500' },
                  { label: t('vulnerabilities.severities.medium'), count: severityCounts.medium, color: 'bg-yellow-500' },
                  { label: t('vulnerabilities.severities.low'), count: severityCounts.low, color: 'bg-blue-400' },
                  { label: t('vulnerabilities.severities.info'), count: severityCounts.info, color: 'bg-gray-400' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span className={`w-3 h-3 rounded-full ${item.color}`}></span>
                    <span className="text-xs text-gray-400">{item.label}: <span className="text-white font-bold">{item.count}</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Attack Chain Ranking Section */}
        {vulnerabilities.length > 0 && showAttackChainRanking && (
          <div className="mb-6">
            <AttackChainRanking
              vulnerabilities={vulnerabilities}
              onVulnClick={(vuln) => {
                handleVulnSelect(vuln);
                openCVEPanel(vuln);
              }}
            />
          </div>
        )}

        {/* AI Assistant Panel */}
        {showAIAssistant && (
          <div className="bg-brand-black-light rounded-xl p-6 border border-purple-500/30 mb-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span>🤖</span> {t('vulnerabilities.aiAssistant')}
            </h3>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <button
                onClick={() => handleAIQuery('Prioriser les vulnérabilités par urgence')}
                className="p-3 bg-brand-black rounded-lg border border-purple-500/30 hover:border-purple-500 transition-all text-left"
              >
                <div className="text-purple-400 font-medium">🎯 Priorisation</div>
                <div className="text-gray-500 text-xs">Urgence et criticité</div>
              </button>
              <button
                onClick={() => handleAIQuery('Quelles vulnérabilités sont exploitables ?')}
                className="p-3 bg-brand-black rounded-lg border border-purple-500/30 hover:border-purple-500 transition-all text-left"
              >
                <div className="text-purple-400 font-medium">💥 Exploitabilité</div>
                <div className="text-gray-500 text-xs">Failles exploitables</div>
              </button>
              <button
                onClick={() => handleAIQuery('Générer un résumé exécutif des vulnérabilités')}
                className="p-3 bg-brand-black rounded-lg border border-purple-500/30 hover:border-purple-500 transition-all text-left"
              >
                <div className="text-purple-400 font-medium">📊 Résumé</div>
                <div className="text-gray-500 text-xs">Vue d'ensemble</div>
              </button>
              <button
                onClick={() => handleAIQuery('Plan de remédiation pour les vulnérabilités')}
                className="p-3 bg-brand-black rounded-lg border border-purple-500/30 hover:border-purple-500 transition-all text-left"
              >
                <div className="text-purple-400 font-medium">🔧 Remédiation</div>
                <div className="text-gray-500 text-xs">Plan de correction</div>
              </button>
            </div>
            <div className="flex gap-3">
              <input
                type="text"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && aiQuery && handleAIQuery(aiQuery)}
                placeholder="Posez une question sur les vulnérabilités..."
                className="flex-1 px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
              />
              <button
                onClick={() => aiQuery && handleAIQuery(aiQuery)}
                disabled={aiQueryLoading || !aiQuery}
                className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all disabled:opacity-50"
              >
                {aiQueryLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : 'Analyser'}
              </button>
            </div>
            {aiResponse && (
              <div className="mt-4 p-4 bg-brand-black rounded-lg border border-purple-500/30">
                <MarkdownRenderer content={aiResponse} variant="compact" />
              </div>
            )}
          </div>
        )}

        {/* PoC Auto Panel */}
        {showPoCPanel && (
          <div className="bg-brand-black-light rounded-xl p-6 border border-red-500/30 mb-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span>💥</span> PoC Auto - Génération et Exécution
            </h3>

            <div className="grid grid-cols-2 gap-6">
              {/* Left: Vulnerability Selection */}
              <div>
                <h4 className="text-lg font-medium text-white mb-3">Sélectionner une vulnérabilité</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {vulnerabilities.filter(v => v.severity === 'critical' || v.severity === 'high').slice(0, 10).map(vuln => (
                    <div
                      key={vuln.id}
                      onClick={() => generatePoC(vuln)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedPoCVuln?.id === vuln.id
                          ? 'bg-red-500/20 border-red-500'
                          : 'bg-brand-black border-gray-700 hover:border-red-500/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={`text-xs px-2 py-1 rounded mr-2 ${getSeverityColor(vuln.severity)}`}>
                            {t(`vulnerabilities.severities.${vuln.severity}`)}
                          </span>
                          <span className="text-white text-sm">{vuln.title?.substring(0, 40)}...</span>
                        </div>
                        {vuln.cve_id && <span className="text-brand-green text-xs">{vuln.cve_id}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                {vulnerabilities.filter(v => v.severity === 'critical' || v.severity === 'high').length === 0 && (
                  <p className="text-gray-500 text-sm">Aucune vulnérabilité critique/haute pour PoC</p>
                )}
              </div>

              {/* Right: PoC Code */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-medium text-white">Code PoC Généré</h4>
                  <div className="flex gap-2">
                    {pocCode && (
                      <>
                        <button
                          onClick={() => navigator.clipboard.writeText(pocCode)}
                          className="px-3 py-1 bg-gray-700 text-gray-300 rounded text-sm hover:bg-gray-600"
                        >
                          Copier
                        </button>
                        <button
                          onClick={executePoC}
                          disabled={pocExecuting}
                          className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 disabled:opacity-50"
                        >
                          {pocExecuting ? 'Exécution...' : 'Exécuter'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {pocLoading ? (
                  <div className="h-64 bg-brand-black rounded-lg border border-gray-700 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <span className="text-gray-400">Génération du PoC...</span>
                    </div>
                  </div>
                ) : pocCode ? (
                  <pre className="h-64 bg-brand-black rounded-lg border border-gray-700 p-4 overflow-auto text-xs text-green-400 font-mono">
                    {pocCode}
                  </pre>
                ) : (
                  <div className="h-64 bg-brand-black rounded-lg border border-gray-700 flex items-center justify-center">
                    <p className="text-gray-500">Sélectionnez une vulnérabilité pour générer un PoC</p>
                  </div>
                )}

                {/* PoC Result */}
                {pocResult && (
                  <div className="mt-3 p-3 bg-brand-black rounded-lg border border-gray-700">
                    <h5 className="text-sm font-medium text-white mb-2">Résultat:</h5>
                    <MarkdownRenderer content={pocResult.output} variant="compact" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Attack Tree Section - Shown when a workspace is selected via sidebar */}
        {activeWorkspaceId && (
          <div className="bg-brand-black-light rounded-xl p-4 border border-purple-500/30 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-lg">📁</span>
                <span className="font-semibold text-white">{activeWorkspace?.name || 'Workspace'}</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowAttackTree(!showAttackTree)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    showAttackTree
                      ? 'bg-brand-green text-brand-black'
                      : 'bg-brand-green/20 text-brand-green border border-brand-green/50 hover:bg-brand-green/30'
                  }`}
                >
                  <span>🌳</span> {showAttackTree ? 'Masquer' : 'Afficher'} Arbre d'Attaque
                </button>
                <button
                  onClick={() => navigate(`/workspaces/${activeWorkspaceId}`)}
                  className="px-3 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/50 rounded text-sm hover:bg-purple-500/30"
                >
                  Voir Workspace
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attack Tree Workflow - n8n Style */}
        {showAttackTree && activeWorkspaceId && (
          <div className="mb-6">
            {attackTreeLoading ? (
              <div className="bg-brand-black rounded-xl border border-brand-green/30 p-12 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-400">Génération de l'arbre d'attaque...</p>
                </div>
              </div>
            ) : attackTreeData ? (
              <AttackTreeWorkflow
                vulnerabilities={attackTreeData.vulnerabilities || workspaceVulns}
                workspaceId={activeWorkspaceId}
                target={attackTreeData.target}
                onExecuteExploit={(vuln) => {
                  setShowPoCPanel(true);
                  generatePoC(vuln);
                }}
                onSelectVulnerability={(vuln) => {
                  handleVulnSelect(vuln);
                  openCVEPanel(vuln);
                }}
              />
            ) : (
              <div className="bg-brand-black rounded-xl border border-brand-green/30 p-8 text-center">
                <p className="text-gray-400">Aucune donnée disponible pour l'arbre d'attaque</p>
                <p className="text-gray-500 text-sm mt-2">Assurez-vous que le workspace a des scans avec des vulnérabilités</p>
              </div>
            )}
          </div>
        )}

        {/* OSINT Intelligence Section */}
        <div className="mb-6">
          <VulnerabilityOSINTInsights workspaceId={activeWorkspaceId} />
        </div>

        {/* Search and Filters */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search */}
            <div className="flex-1 min-w-[300px]">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('vulnerabilities.searchPlaceholder')}
                className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white"
            >
              <option value="severity">{t('vulnerabilities.sortBySeverity')}</option>
              <option value="cvss">{t('vulnerabilities.sortByCvss')}</option>
              <option value="date">{t('vulnerabilities.sortByDate')}</option>
            </select>

            {/* Severity Filters */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filter === 'all' ? 'bg-brand-green text-brand-black' : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-brand-green/50'
                }`}
              >
                {t('common.all')} ({vulnerabilities.length})
              </button>
              <button
                onClick={() => setFilter('critical')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filter === 'critical' ? 'bg-red-500 text-white' : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-red-500/50'
                }`}
              >
                {t('vulnerabilities.severities.critical')} ({severityCounts.critical})
              </button>
              <button
                onClick={() => setFilter('high')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filter === 'high' ? 'bg-orange-500 text-white' : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-orange-500/50'
                }`}
              >
                {t('vulnerabilities.severities.high')} ({severityCounts.high})
              </button>
              <button
                onClick={() => setFilter('medium')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filter === 'medium' ? 'bg-yellow-500 text-white' : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-yellow-500/50'
                }`}
              >
                {t('vulnerabilities.severities.medium')} ({severityCounts.medium})
              </button>
              <button
                onClick={() => setFilter('low')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filter === 'low' ? 'bg-blue-500 text-white' : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-blue-500/50'
                }`}
              >
                {t('vulnerabilities.severities.low')} ({severityCounts.low})
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedVulns.length > 0 && (
            <div className="mt-4 flex items-center gap-3 p-3 bg-brand-green/10 rounded-lg border border-brand-green/30">
              <span className="text-brand-green font-medium">{selectedVulns.length} {t('vulnerabilities.selected')}</span>
              <button
                onClick={exportSelectedJSON}
                className="px-3 py-1 bg-brand-green/20 text-brand-green rounded text-sm hover:bg-brand-green/30"
              >
                {t('vulnerabilities.exportJson')}
              </button>
              <button
                onClick={exportCSV}
                className="px-3 py-1 bg-brand-green/20 text-brand-green rounded text-sm hover:bg-brand-green/30"
              >
                {t('vulnerabilities.exportCsv')}
              </button>
              <button
                onClick={() => setSelectedVulns([])}
                className="px-3 py-1 bg-gray-700 text-gray-300 rounded text-sm hover:bg-gray-600"
              >
                {t('common.deselect')}
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex gap-6">
          {/* Vulnerabilities List */}
          <div className="flex-1 space-y-4">
            {filteredVulns.length > 0 ? (
              filteredVulns.map((vuln) => (
                <div
                  key={vuln.id}
                  onClick={() => {
                    handleVulnSelect(vuln);
                    openCVEPanel(vuln);
                  }}
                  className={`bg-brand-black-light rounded-xl p-6 border transition-all duration-300 cursor-pointer ${
                    selectedVuln?.id === vuln.id
                      ? 'border-brand-green shadow-glow-green'
                      : 'border-brand-green/20 hover:border-brand-green/50 hover:shadow-glow-green'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedVulns.some(v => v.id === vuln.id)}
                        onChange={(e) => toggleVulnSelection(vuln, e)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 w-5 h-5 rounded border-gray-600 bg-brand-black text-brand-green focus:ring-brand-green"
                      />

                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className={`badge border ${getSeverityColor(vuln.severity)}`}>
                            {t(`vulnerabilities.severities.${vuln.severity}`)}
                          </span>
                          {vuln.cve_id && (
                            <span className="badge bg-brand-green/20 text-brand-green border border-brand-green/50">
                              {vuln.cve_id}
                            </span>
                          )}
                          {vuln.cvss_score && (
                            <span className={`text-sm px-2 py-1 rounded ${
                              vuln.cvss_score >= 9 ? 'bg-red-500/20 text-red-400' :
                              vuln.cvss_score >= 7 ? 'bg-orange-500/20 text-orange-400' :
                              vuln.cvss_score >= 4 ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {t('vulnerabilities.cvss')}: {vuln.cvss_score}
                            </span>
                          )}
                          {vuln.cvss_score >= 7 && (
                            <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">
                              ⚠️ {t('vulnerabilities.exploitable')}
                            </span>
                          )}
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2 break-words">{vuln.title}</h3>
                        <p className="text-gray-400 text-sm mb-3 line-clamp-2 break-all">
                          {vuln.description || 'No description available'}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                          <span>Hôte: <span className="text-brand-green">{vuln.host_id}</span></span>
                          <span>Découvert: {new Date(vuln.discovered_at || Date.now()).toLocaleDateString('fr-FR')}</span>
                          {vuln.scan_target && <span>Cible: {vuln.scan_target}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      {/* Priority Badge */}
                      {(() => {
                        const priorityInfo = getPriorityInfo(vuln);
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openPriorityModal(vuln);
                            }}
                            className={`px-3 py-1 ${priorityInfo.bgColor} ${priorityInfo.color} border ${priorityInfo.borderColor} rounded text-sm hover:opacity-80 flex items-center gap-1`}
                            title={vuln.priority_note || 'Cliquer pour modifier la priorité'}
                          >
                            <span>P: {priorityInfo.label}</span>
                          </button>
                        );
                      })()}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/vulnerabilities/${vuln.id}`);
                        }}
                        className="px-3 py-1 text-brand-green hover:text-brand-green-light transition-colors text-sm"
                      >
                        Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openActionPanel(vuln);
                        }}
                        className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded text-sm hover:bg-orange-500/30 flex items-center gap-1"
                      >
                        Actions
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPoCPanel(true);
                          generatePoC(vuln);
                        }}
                        className="px-3 py-1 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30"
                      >
                        PoC
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-brand-black-light rounded-xl p-12 border border-brand-green/20 text-center">
                <div className="text-6xl mb-4">🛡️</div>
                <h3 className="text-2xl font-bold text-gray-300 mb-2">{t('vulnerabilities.noVulnerabilities')}</h3>
                <p className="text-gray-500">
                  {filter === 'all'
                    ? t('vulnerabilities.noVulnerabilitiesFound')
                    : t('vulnerabilities.noVulnerabilitiesBySeverity', { severity: filter })
                  }
                </p>
              </div>
            )}
          </div>

          {/* Right Side Panel */}
          <div className="w-80 space-y-4">
            {/* Actions Panel */}
            <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <span>⚡</span> Actions Rapides
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => window.location.href = '/scans'}
                  className="w-full px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-all text-sm"
                >
                  Nouveau scan
                </button>
                <PlanRestrictedButton
                  feature="custom_reports"
                  onClick={() => window.location.href = '/reports'}
                  className="w-full px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/50 rounded-lg hover:bg-purple-500/30 transition-all text-sm"
                  disabledClassName="!bg-gray-700/50 !text-gray-500 !border-gray-600 cursor-not-allowed"
                >
                  Générer rapport
                </PlanRestrictedButton>
                <PlanRestrictedButton
                  feature="export_data"
                  onClick={exportSelectedJSON}
                  className="w-full px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-all text-sm"
                  disabledClassName="!bg-gray-700/50 !text-gray-500 !border-gray-600 cursor-not-allowed"
                >
                  Exporter JSON
                </PlanRestrictedButton>
                <PlanRestrictedButton
                  feature="export_data"
                  onClick={exportCSV}
                  className="w-full px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-all text-sm"
                  disabledClassName="!bg-gray-700/50 !text-gray-500 !border-gray-600 cursor-not-allowed"
                >
                  Exporter CSV
                </PlanRestrictedButton>
              </div>
            </div>

            {/* AI Analysis Panel */}
            <div className="bg-brand-black-light rounded-xl p-4 border border-purple-500/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <span>🤖</span> Analyse IA
              </h3>
              {selectedVuln ? (
                <div className="space-y-3">
                  <div className="text-sm text-gray-400">
                    <span className="text-white font-medium">Sélectionné:</span>
                    <p className="text-brand-green truncate">{selectedVuln.title}</p>
                  </div>
                  <button
                    onClick={requestAiAnalysis}
                    disabled={aiLoading}
                    className="w-full px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/50 rounded-lg hover:bg-purple-500/30 transition-all text-sm disabled:opacity-50"
                  >
                    {aiLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                        Analyse en cours...
                      </span>
                    ) : 'Analyser avec IA'}
                  </button>

                  {aiAnalysis && (
                    <div className="mt-3 space-y-3 text-sm">
                      <div className="p-3 bg-brand-black rounded-lg border border-gray-700">
                        <h4 className="text-purple-400 font-medium mb-1">Résumé</h4>
                        <MarkdownRenderer content={aiAnalysis.summary} variant="compact" />
                      </div>
                      <div className="p-3 bg-brand-black rounded-lg border border-gray-700">
                        <h4 className="text-purple-400 font-medium mb-1">Impact</h4>
                        <MarkdownRenderer content={aiAnalysis.impact} variant="compact" />
                      </div>
                      <div className="p-3 bg-brand-black rounded-lg border border-gray-700">
                        <h4 className="text-purple-400 font-medium mb-1">Exploitabilité</h4>
                        <span className={`text-xs px-2 py-1 rounded ${
                          aiAnalysis.exploitability === 'Élevée' ? 'bg-red-500/20 text-red-400' :
                          aiAnalysis.exploitability === 'Moyenne' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {aiAnalysis.exploitability}
                        </span>
                        <span className="text-gray-400 text-xs ml-2">({aiAnalysis.attackVector})</span>
                      </div>
                      <div className="p-3 bg-brand-black rounded-lg border border-gray-700">
                        <h4 className="text-purple-400 font-medium mb-2">MITRE ATT&CK</h4>
                        <div className="space-y-1">
                          {aiAnalysis.mitreTechniques?.map((tech, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <span className="text-red-400">{tech.id}</span>
                              <span className="text-gray-400">{tech.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="p-3 bg-brand-black rounded-lg border border-gray-700">
                        <h4 className="text-purple-400 font-medium mb-2">Recommandations</h4>
                        <ul className="text-gray-300 text-xs space-y-1">
                          {aiAnalysis.recommendations.map((rec, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-brand-green">•</span>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Sélectionnez une vulnérabilité pour obtenir une analyse IA détaillée.</p>
              )}
            </div>

            {/* Statistics Panel */}
            <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <span>📊</span> Statistiques
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">{t('vulnerabilities.attackChain.totalVulns')}</span>
                  <span className="text-white font-bold">{vulnerabilities.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-red-500">{t('vulnerabilities.severities.critical')}</span>
                  <span className="text-white font-bold">{severityCounts.critical}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-orange-500">{t('vulnerabilities.severities.high')}</span>
                  <span className="text-white font-bold">{severityCounts.high}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-yellow-500">{t('vulnerabilities.severities.medium')}</span>
                  <span className="text-white font-bold">{severityCounts.medium}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-blue-400">{t('vulnerabilities.severities.low')}</span>
                  <span className="text-white font-bold">{severityCounts.low}</span>
                </div>
                <div className="border-t border-gray-700 my-2"></div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">CVSS Moyen</span>
                  <span className="text-brand-green font-bold">{avgCVSS}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Exploitables</span>
                  <span className="text-red-400 font-bold">{exploitableCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Hôtes Affectés</span>
                  <span className="text-purple-400 font-bold">{uniqueHosts}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Attack Path Diagram - En bas de page */}
        {vulnerabilities.length > 0 && (
          <div className="mt-8">
            <AttackPathDiagram vulnerabilities={vulnerabilities} />
          </div>
        )}

        {/* Actions History Panel */}
        {vulnActions.length > 0 && (
          <div className="mt-6 bg-brand-black-light rounded-xl p-6 border border-orange-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span>📋</span> Historique des Actions ({vulnActions.length})
              </h3>
              <button
                onClick={() => setVulnActions([])}
                className="text-xs text-gray-400 hover:text-red-400"
              >
                Effacer l'historique
              </button>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {vulnActions.map(action => (
                <div key={action.id} className="bg-brand-black rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                        action.action_type === 'exploit' ? 'bg-red-500/20 text-red-400' :
                        action.action_type === 'inject' ? 'bg-purple-500/20 text-purple-400' :
                        action.action_type === 'enum' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-orange-500/20 text-orange-400'
                      }`}>
                        {action.action_type.toUpperCase()}
                      </span>
                      <span className="text-white font-medium text-sm truncate">{action.vuln_title}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(action.timestamp).toLocaleTimeString('fr-FR')}
                    </span>
                  </div>
                  <pre className="text-xs text-gray-400 bg-gray-900 p-2 rounded overflow-x-auto">
                    {action.result?.output || 'Action en cours...'}
                  </pre>
                  {action.scan_id && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-gray-500">Scan:</span>
                      <button
                        onClick={() => navigate(`/scans/${action.scan_id}`)}
                        className="text-xs text-cyan-400 hover:underline"
                      >
                        Voir le scan
                      </button>
                      <button
                        onClick={() => navigate(`/reports?scan_id=${action.scan_id}`)}
                        className="text-xs text-purple-400 hover:underline"
                      >
                        Ajouter au rapport
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Panel Modal */}
      {showActionPanel && actionVuln && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowActionPanel(false)}>
          <div className="bg-brand-black-light rounded-xl border-2 border-orange-500/50 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-orange-500/30">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>⚡</span> {t('vulnerabilities.actionPanel.title')}
                  </h2>
                  <p className="text-gray-400 text-sm mt-1 truncate">{actionVuln.title}</p>
                </div>
                <button
                  onClick={() => setShowActionPanel(false)}
                  className="p-2 hover:bg-brand-black rounded-lg transition-colors flex-shrink-0"
                >
                  <span className="text-gray-400 text-xl">×</span>
                </button>
              </div>
            </div>

            {/* Vuln Info */}
            <div className="p-6 border-b border-gray-700">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <span className="text-xs text-gray-500">Sévérité</span>
                  <div className={`badge border mt-1 ${getSeverityColor(actionVuln.severity)}`}>
                    {t(`vulnerabilities.severities.${actionVuln.severity}`)}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500">CVE</span>
                  <div className="text-brand-green font-mono mt-1">{actionVuln.cve_id || 'N/A'}</div>
                </div>
                <div>
                  <span className="text-xs text-gray-500">CVSS</span>
                  <div className="text-white font-bold mt-1">{actionVuln.cvss_score || 'N/A'}</div>
                </div>
              </div>
              <div className="mt-3">
                <span className="text-xs text-gray-500">Cible</span>
                <div className="text-cyan-400 font-mono mt-1">{actionVuln.host_id || actionVuln.scan_target}</div>
              </div>
            </div>

            {/* Actions Grid */}
            <div className="p-6">
              <h3 className="text-lg font-medium text-white mb-4">Actions disponibles</h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Exploit */}
                <button
                  onClick={() => {
                    executeVulnAction('exploit', actionVuln);
                    setShowActionPanel(false);
                  }}
                  disabled={actionLoading}
                  className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl hover:bg-red-500/20 hover:border-red-500 transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">💥</span>
                    <span className="text-red-400 font-bold text-lg group-hover:text-red-300">Exploitation</span>
                  </div>
                  <p className="text-gray-500 text-sm">Tenter d'exploiter cette vulnérabilité pour obtenir un accès</p>
                </button>

                {/* Injection */}
                <button
                  onClick={() => {
                    executeVulnAction('inject', actionVuln);
                    setShowActionPanel(false);
                  }}
                  disabled={actionLoading}
                  className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl hover:bg-purple-500/20 hover:border-purple-500 transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">💉</span>
                    <span className="text-purple-400 font-bold text-lg group-hover:text-purple-300">Injection</span>
                  </div>
                  <p className="text-gray-500 text-sm">Exécuter une injection (SQL, commande, XSS, etc.)</p>
                </button>

                {/* Enumeration */}
                <button
                  onClick={() => {
                    executeVulnAction('enum', actionVuln);
                    setShowActionPanel(false);
                  }}
                  disabled={actionLoading}
                  className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl hover:bg-blue-500/20 hover:border-blue-500 transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">🔍</span>
                    <span className="text-blue-400 font-bold text-lg group-hover:text-blue-300">Énumération</span>
                  </div>
                  <p className="text-gray-500 text-sm">Collecter plus d'informations sur cette faille</p>
                </button>

                {/* Privilege Escalation */}
                <button
                  onClick={() => {
                    executeVulnAction('privesc', actionVuln);
                    setShowActionPanel(false);
                  }}
                  disabled={actionLoading}
                  className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl hover:bg-yellow-500/20 hover:border-yellow-500 transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">⬆️</span>
                    <span className="text-yellow-400 font-bold text-lg group-hover:text-yellow-300">Élévation</span>
                  </div>
                  <p className="text-gray-500 text-sm">Tenter une élévation de privilèges</p>
                </button>

                {/* Lateral Movement */}
                <button
                  onClick={() => {
                    executeVulnAction('lateral', actionVuln);
                    setShowActionPanel(false);
                  }}
                  disabled={actionLoading}
                  className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl hover:bg-cyan-500/20 hover:border-cyan-500 transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">🔀</span>
                    <span className="text-cyan-400 font-bold text-lg group-hover:text-cyan-300">Pivot</span>
                  </div>
                  <p className="text-gray-500 text-sm">Pivoter vers d'autres systèmes du réseau</p>
                </button>

                {/* Data Exfil */}
                <button
                  onClick={() => {
                    executeVulnAction('exfil', actionVuln);
                    setShowActionPanel(false);
                  }}
                  disabled={actionLoading}
                  className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl hover:bg-green-500/20 hover:border-green-500 transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">📤</span>
                    <span className="text-green-400 font-bold text-lg group-hover:text-green-300">Extraction</span>
                  </div>
                  <p className="text-gray-500 text-sm">Extraire des données sensibles via cette faille</p>
                </button>
              </div>

              {/* AI Wizard & Screenshot Section */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                {/* AI Wizard Button */}
                <button
                  onClick={() => {
                    analyzeVulnForWizard(actionVuln);
                  }}
                  disabled={aiWizardLoading}
                  className="p-4 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border-2 border-purple-500/50 rounded-xl hover:from-purple-500/30 hover:to-cyan-500/30 hover:border-purple-400 transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">🤖</span>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400 font-bold text-lg">Wizard IA</span>
                  </div>
                  <p className="text-gray-400 text-sm">L'IA analyse la CVE et lance le wizard d'exploitation adapté</p>
                </button>

                {/* Screenshot Capture Button */}
                <button
                  onClick={() => {
                    captureScreenshot(actionVuln, `Preuve d'exploitation - ${actionVuln.cve_id || actionVuln.title}`);
                    setShowActionPanel(false);
                  }}
                  disabled={captureLoading}
                  className="p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 rounded-xl hover:from-green-500/30 hover:to-emerald-500/30 hover:border-green-400 transition-all text-left group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">📸</span>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400 font-bold text-lg">Capture Preuve</span>
                  </div>
                  <p className="text-gray-400 text-sm">Capturer une preuve d'exploitation pour le rapport</p>
                </button>
              </div>

              {/* Custom Action */}
              <div className="mt-6 p-4 bg-brand-black rounded-xl border border-gray-700">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <span>🛠️</span> Action personnalisee
                </h4>
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder={t('vulnerabilities.customActionPlaceholder')}
                    className="flex-1 px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none text-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        executeVulnAction(e.target.value, actionVuln);
                        setShowActionPanel(false);
                      }
                    }}
                  />
                  <button
                    onClick={(e) => {
                      const input = e.target.previousSibling;
                      if (input.value) {
                        executeVulnAction(input.value, actionVuln);
                        setShowActionPanel(false);
                      }
                    }}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all text-sm font-medium"
                  >
                    Exécuter
                  </button>
                </div>
              </div>

              {/* Info about report integration */}
              <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <p className="text-purple-400 text-sm flex items-center gap-2">
                  <span>📝</span>
                  Les resultats des actions seront automatiquement integres au rapport du scan
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Priority Modal */}
      {showPriorityModal && priorityVuln && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={closePriorityModal}>
          <div className="bg-brand-black-light rounded-xl border-2 border-brand-green/50 w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-brand-green/30">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>🎯</span> Modifier la Priorité
                  </h2>
                  <p className="text-gray-400 text-sm mt-1 truncate max-w-sm">{priorityVuln.title}</p>
                </div>
                <button
                  onClick={closePriorityModal}
                  className="p-2 hover:bg-brand-black rounded-lg transition-colors"
                >
                  <span className="text-gray-400 text-xl">x</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Current severity info */}
              <div className="p-3 bg-brand-black rounded-lg border border-gray-700">
                <span className="text-xs text-gray-500">Sévérité automatique :</span>
                <div className={`badge border mt-1 ${getSeverityColor(priorityVuln.severity)}`}>
                  {t(`vulnerabilities.severities.${priorityVuln.severity}`)}
                </div>
                {priorityVuln.cvss_score && (
                  <span className="ml-2 text-gray-400">CVSS: {priorityVuln.cvss_score}</span>
                )}
              </div>

              {/* Priority Selection */}
              <div>
                <label className="text-white font-medium mb-3 block">Priorité manuelle</label>
                <div className="grid grid-cols-3 gap-3">
                  {/* Auto option */}
                  <button
                    onClick={() => setSelectedPriority(null)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      selectedPriority === null
                        ? 'bg-gray-500/30 border-gray-400 text-white'
                        : 'bg-brand-black border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-lg mb-1">🤖</div>
                    <div className="text-sm font-medium">Auto</div>
                    <div className="text-xs text-gray-500">Utilise CVSS</div>
                  </button>

                  {/* Priority 1 - Critique */}
                  <button
                    onClick={() => setSelectedPriority(1)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      selectedPriority === 1
                        ? 'bg-red-500/30 border-red-500 text-red-400'
                        : 'bg-brand-black border-gray-700 text-gray-400 hover:border-red-500/50'
                    }`}
                  >
                    <div className="text-lg mb-1">🔴</div>
                    <div className="text-sm font-medium">Critique</div>
                    <div className="text-xs text-gray-500">P1</div>
                  </button>

                  {/* Priority 2 - Haute */}
                  <button
                    onClick={() => setSelectedPriority(2)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      selectedPriority === 2
                        ? 'bg-orange-500/30 border-orange-500 text-orange-400'
                        : 'bg-brand-black border-gray-700 text-gray-400 hover:border-orange-500/50'
                    }`}
                  >
                    <div className="text-lg mb-1">🟠</div>
                    <div className="text-sm font-medium">Haute</div>
                    <div className="text-xs text-gray-500">P2</div>
                  </button>

                  {/* Priority 3 - Moyenne */}
                  <button
                    onClick={() => setSelectedPriority(3)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      selectedPriority === 3
                        ? 'bg-yellow-500/30 border-yellow-500 text-yellow-400'
                        : 'bg-brand-black border-gray-700 text-gray-400 hover:border-yellow-500/50'
                    }`}
                  >
                    <div className="text-lg mb-1">🟡</div>
                    <div className="text-sm font-medium">Moyenne</div>
                    <div className="text-xs text-gray-500">P3</div>
                  </button>

                  {/* Priority 4 - Basse */}
                  <button
                    onClick={() => setSelectedPriority(4)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      selectedPriority === 4
                        ? 'bg-blue-500/30 border-blue-500 text-blue-400'
                        : 'bg-brand-black border-gray-700 text-gray-400 hover:border-blue-500/50'
                    }`}
                  >
                    <div className="text-lg mb-1">🔵</div>
                    <div className="text-sm font-medium">Basse</div>
                    <div className="text-xs text-gray-500">P4</div>
                  </button>

                  {/* Priority 5 - Info */}
                  <button
                    onClick={() => setSelectedPriority(5)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      selectedPriority === 5
                        ? 'bg-gray-500/30 border-gray-400 text-gray-300'
                        : 'bg-brand-black border-gray-700 text-gray-400 hover:border-gray-500/50'
                    }`}
                  >
                    <div className="text-lg mb-1">⚪</div>
                    <div className="text-sm font-medium">Info</div>
                    <div className="text-xs text-gray-500">P5</div>
                  </button>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="text-white font-medium mb-2 block">Note (optionnel)</label>
                <textarea
                  value={priorityNote}
                  onChange={(e) => setPriorityNote(e.target.value)}
                  placeholder="Expliquez pourquoi vous avez changé la priorité..."
                  className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none resize-none"
                  rows={3}
                  maxLength={500}
                />
                <div className="text-right text-xs text-gray-500 mt-1">{priorityNote.length}/500</div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={closePriorityModal}
                className="px-4 py-2 bg-brand-black border border-gray-700 text-gray-400 rounded-lg hover:border-gray-500 transition-all"
              >
                Annuler
              </button>
              <button
                onClick={savePriority}
                disabled={prioritySaving}
                className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green-light transition-all font-medium disabled:opacity-50"
              >
                {prioritySaving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-brand-black border-t-transparent rounded-full animate-spin"></div>
                    Enregistrement...
                  </span>
                ) : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CVE Detail Panel */}
      <CVEDetailPanel
        cve={selectedCVE}
        isOpen={isPanelOpen}
        onClose={closeCVEPanel}
      />

      {/* AI Wizard Recommendation Modal */}
      {showAIWizard && wizardVuln && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowAIWizard(false)}>
          <div className="bg-brand-black-light rounded-xl border-2 border-purple-500/50 w-full max-w-2xl mx-4" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-purple-500/30 bg-gradient-to-r from-purple-500/10 to-cyan-500/10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>🤖</span> Wizard IA - Analyse de Vulnérabilité
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">{wizardVuln.cve_id || wizardVuln.title}</p>
                </div>
                <button
                  onClick={() => setShowAIWizard(false)}
                  className="p-2 hover:bg-brand-black rounded-lg transition-colors"
                >
                  <span className="text-gray-400 text-xl">x</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {aiWizardLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-purple-400 font-medium">Analyse IA en cours...</p>
                  <p className="text-gray-500 text-sm mt-2">L'IA détermine le meilleur wizard d'exploitation</p>
                </div>
              ) : aiWizardRecommendation ? (
                <div className="space-y-6">
                  {/* Recommendation */}
                  <div className={`p-6 rounded-xl border-2 ${
                    aiWizardRecommendation.color === 'purple' ? 'bg-purple-500/10 border-purple-500/50' :
                    aiWizardRecommendation.color === 'blue' ? 'bg-blue-500/10 border-blue-500/50' :
                    aiWizardRecommendation.color === 'green' ? 'bg-green-500/10 border-green-500/50' :
                    aiWizardRecommendation.color === 'orange' ? 'bg-orange-500/10 border-orange-500/50' :
                    aiWizardRecommendation.color === 'red' ? 'bg-red-500/10 border-red-500/50' :
                    'bg-cyan-500/10 border-cyan-500/50'
                  }`}>
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-4xl">{aiWizardRecommendation.icon}</span>
                      <div>
                        <h3 className={`text-xl font-bold ${
                          aiWizardRecommendation.color === 'purple' ? 'text-purple-400' :
                          aiWizardRecommendation.color === 'blue' ? 'text-blue-400' :
                          aiWizardRecommendation.color === 'green' ? 'text-green-400' :
                          aiWizardRecommendation.color === 'orange' ? 'text-orange-400' :
                          aiWizardRecommendation.color === 'red' ? 'text-red-400' :
                          'text-cyan-400'
                        }`}>
                          {aiWizardRecommendation.name}
                        </h3>
                        <p className="text-gray-400 text-sm">Wizard recommande par l'IA</p>
                      </div>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {aiWizardRecommendation.reason}
                    </p>
                  </div>

                  {/* Vuln Summary */}
                  <div className="p-4 bg-brand-black rounded-xl border border-gray-700">
                    <h4 className="text-white font-medium mb-3">Résumé de la vulnérabilité</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">CVE:</span>
                        <span className="text-cyan-400 ml-2 font-mono">{wizardVuln.cve_id || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">CVSS:</span>
                        <span className="text-white ml-2 font-bold">{wizardVuln.cvss_score || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Sévérité\u202F:</span>
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(wizardVuln.severity)}`}>
                          {t(`vulnerabilities.severities.${wizardVuln.severity}`)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Cible:</span>
                        <span className="text-green-400 ml-2 font-mono text-xs">{wizardVuln.host_id || wizardVuln.scan_target}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <button
                      onClick={() => launchWizard(aiWizardRecommendation.wizard, wizardVuln)}
                      className={`flex-1 py-3 rounded-xl font-bold text-white transition-all ${
                        aiWizardRecommendation.color === 'purple' ? 'bg-purple-600 hover:bg-purple-500' :
                        aiWizardRecommendation.color === 'blue' ? 'bg-blue-600 hover:bg-blue-500' :
                        aiWizardRecommendation.color === 'green' ? 'bg-green-600 hover:bg-green-500' :
                        aiWizardRecommendation.color === 'orange' ? 'bg-orange-600 hover:bg-orange-500' :
                        aiWizardRecommendation.color === 'red' ? 'bg-red-600 hover:bg-red-500' :
                        'bg-cyan-600 hover:bg-cyan-500'
                      }`}
                    >
                      Lancer {aiWizardRecommendation.name}
                    </button>
                    <button
                      onClick={() => setShowAIWizard(false)}
                      className="px-6 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-all"
                    >
                      Annuler
                    </button>
                  </div>

                  {/* Other Wizards */}
                  <div className="border-t border-gray-700 pt-4">
                    <h4 className="text-gray-400 text-sm mb-3">Ou choisir un autre wizard :</h4>
                    <div className="flex flex-wrap gap-2">
                      {['ad', 'api', 'iot', 'ot', 'expert', 'ai'].filter(w => w !== aiWizardRecommendation.wizard).map(w => (
                        <button
                          key={w}
                          onClick={() => launchWizard(w, wizardVuln)}
                          className="px-3 py-1.5 bg-brand-black border border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 text-sm transition-all"
                        >
                          {w === 'ad' ? '🏢 AD' : w === 'api' ? '🔌 API' : w === 'iot' ? '📡 IoT' : w === 'ot' ? '🏭 OT' : w === 'expert' ? '⚡ Expert' : '🤖 AI'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Wizard Modals */}
      {selectedWizard === 'ad' && (
        <ADAttackWizard
          isOpen={true}
          onClose={() => setSelectedWizard(null)}
          initialTarget={wizardVuln?.host_id || wizardVuln?.scan_target}
          vulnerability={wizardVuln}
          workspaceId={activeWorkspaceId}
        />
      )}

      {selectedWizard === 'expert' && (
        <ExpertScanWizard
          isOpen={true}
          onClose={() => setSelectedWizard(null)}
          vulnerability={wizardVuln}
          workspaceId={wizardVuln?.workspace_id}
        />
      )}

      {selectedWizard === 'ai' && (
        <AIScanWizard
          isOpen={true}
          onClose={() => setSelectedWizard(null)}
          vulnerability={wizardVuln}
          workspaceId={wizardVuln?.workspace_id}
        />
      )}

      {selectedWizard === 'api' && (
        <APIScanWizard
          isOpen={true}
          onClose={() => setSelectedWizard(null)}
          vulnerability={wizardVuln}
          workspaceId={wizardVuln?.workspace_id}
        />
      )}

      {selectedWizard === 'iot' && (
        <IoTScanWizard
          isOpen={true}
          onClose={() => setSelectedWizard(null)}
          vulnerability={wizardVuln}
          workspaceId={wizardVuln?.workspace_id}
        />
      )}

      {selectedWizard === 'ot' && (
        <OTScanWizard
          isOpen={true}
          onClose={() => setSelectedWizard(null)}
          vulnerability={wizardVuln}
          workspaceId={wizardVuln?.workspace_id}
        />
      )}

      {/* Screenshots Panel */}
      {showScreenshotPanel && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowScreenshotPanel(false)}>
          <div className="bg-brand-black-light rounded-xl border-2 border-green-500/50 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-green-500/30">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span>📸</span> Captures de Preuves ({screenshots.length})
                </h2>
                <button
                  onClick={() => setShowScreenshotPanel(false)}
                  className="p-2 hover:bg-brand-black rounded-lg transition-colors"
                >
                  <span className="text-gray-400 text-xl">x</span>
                </button>
              </div>
            </div>
            <div className="p-6">
              {screenshots.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <span className="text-4xl mb-4 block">📷</span>
                  <p>Aucune capture de preuve</p>
                  <p className="text-sm mt-2">Les captures seront automatiquement ajoutees aux rapports</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {screenshots.map((screenshot, idx) => (
                    <div key={idx} className="bg-brand-black rounded-xl p-4 border border-gray-700">
                      <div className="aspect-video bg-gray-900 rounded-lg mb-3 flex items-center justify-center">
                        {screenshot.path?.endsWith('.txt') ? (
                          <span className="text-4xl">📝</span>
                        ) : (
                          <img src={screenshot.path} alt={screenshot.description} className="w-full h-full object-contain rounded-lg" />
                        )}
                      </div>
                      <p className="text-white text-sm font-medium truncate">{screenshot.description}</p>
                      <p className="text-gray-500 text-xs mt-1">{screenshot.captured_at}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VulnerabilitiesList;
